import { useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useReactToPrint } from 'react-to-print';

import useAuthStore from '@entities/auth/model/useAuthStore';
import { DOCUMENT_DETAIL_QUERY_KEY } from './useDocumentDetail';
import {
  returnDocument,
  removeApprovalDoc,
  postApprovalSign,
  postApprovalReviewerDone,
  postApprovalAgreementDone,
} from '@entities/approval/api/approval';
import {
  addDocumentComment,
  modifyDocumentComment,
  deleteDocumentComment,
  updateExecutorList,
  updateReceiverList,
  downloadDocumentFile,
  updateApprovalDocDate,
  removeDocument,
} from '@entities/document/api/document';
import { DOCUMENT_STATE } from '@entities/approval/constants';
import calculatePageCount, {
  calculateImageSlices,
  splitEditorContentByPage,
} from '@features/document-write/utils/calculatePageCount';
import normalizeHtmlForCKEditor from '@shared/utils/normalizeHtmlForCKEditor';
import {
  captureHtmlAsDataUrl,
  DOCUMENT_CAPTURE_DEFAULTS,
} from '@shared/utils/captureDocumentImage';
import getApiErrorMessage from '@shared/lib/getApiErrorMessage';

/**
 * useDocumentActions 훅
 * - 문서 액션 핸들러 (수정, 삭제, 반려, 수정요청 등)
 * - Alert 상태 관리
 * - InputModal 상태 관리
 */
function useDocumentActions({
  docIdx,
  docData,
  documentInfo,
  referenceDocuments,
  attachments,
  onClose,
  onSaveSuccess,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  // enumber는 user 객체 내부가 아닌 별도 필드로 저장됨
  const enumber = useAuthStore((state) => state.enumber);
  const companyCode = useAuthStore((state) => {
    const code = state.companyCode;
    return code ? Number(code) : null;
  });

  // Alert 상태
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
    onConfirm: null, // 확인 버튼 클릭 시 콜백
  });

  // 수정요청 모달 상태
  const [isInputModalOpen, setIsInputModalOpen] = useState(false);
  const [inputModalValue, setInputModalValue] = useState('');

  // 반려 모달 상태
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectModalValue, setRejectModalValue] = useState('');

  // 결재 확인 모달 상태
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);

  // 코멘트 작성 모달 상태
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [commentModalValue, setCommentModalValue] = useState('');

  // 코멘트 상세 모달 상태
  const [isCommentDetailModalOpen, setIsCommentDetailModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);

  // 코멘트 수정 모달 상태
  const [isCommentEditModalOpen, setIsCommentEditModalOpen] = useState(false);
  const [commentEditValue, setCommentEditValue] = useState('');

  // 중첩된 모달을 위한 상태 (재귀적 렌더링 지원)
  const [stackedDocIds, setStackedDocIds] = useState([]);

  // 페이지 수 및 분할된 에디터 콘텐츠 상태 추가
  const [calculatedTotalPages, setCalculatedTotalPages] = useState(1);
  const [editorContentByPage, setEditorContentByPage] = useState([]);
  const [previewImageData, setPreviewImageData] = useState(null);

  // 다운로드 상태 추가
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  // 기안일자 수정 상태 (어드민 전용)
  const [editedDraftDate, setEditedDraftDate] = useState(null);
  const [isSavingDraftDate, setIsSavingDraftDate] = useState(false);

  // 현재 사용자의 결재 위치 찾기 (sign_n, check_n, agreement)
  const myPosition = useMemo(
    function () {
      if (!docData || !enumber) {
        return null;
      }
      const myEnumber = Number(enumber);

      // 1. 결재자 (sign_1은 기안자이므로 제외)
      for (let i = 2; i <= 5; i++) {
        const signEnumber = Number(docData[`sign_${i}`]);
        const signState = Number(docData[`sign_${i}_state`]);

        if (signEnumber === myEnumber) {
          // WAITSIGN(1) 또는 SIGNING(2) 상태인 경우 (결재 대기 또는 결재 중)
          if (signState === DOCUMENT_STATE.WAITSIGN || signState === DOCUMENT_STATE.SIGNING) {
            return `sign${i}`;
          }
        }
      }

      // 2. 검토자
      for (let i = 1; i <= 4; i++) {
        const checkEnumber = Number(docData[`check_${i}`]);
        const checkState = Number(docData[`check_${i}_state`]);

        if (checkEnumber === myEnumber) {
          // WAITSIGN(1) 또는 SIGNING(2) 상태인 경우
          if (checkState === DOCUMENT_STATE.WAITSIGN || checkState === DOCUMENT_STATE.SIGNING) {
            return `check${i}`;
          }
        }
      }

      // 3. 합의자
      const agreementEnumber = Number(docData.agreement);
      const agreementState = Number(docData.agreement_state);

      if (agreementEnumber === myEnumber) {
        // WAITSIGN(1) 또는 SIGNING(2) 상태인 경우
        if (
          agreementState === DOCUMENT_STATE.WAITSIGN ||
          agreementState === DOCUMENT_STATE.SIGNING
        ) {
          return 'agreement';
        }
      }

      return null;
    },
    [docData, enumber],
  );

  // 기안일자 수정이 진행 중이면 해당 날짜를 반영
  const finalDocumentInfo = useMemo(() => {
    if (!documentInfo) return null;
    if (editedDraftDate) {
      return {
        ...documentInfo,
        draftDate: editedDraftDate,
      };
    }
    return documentInfo;
  }, [documentInfo, editedDraftDate]);

  // Alert 표시 함수 (onConfirm 콜백 지원)
  function showAlert(message, onConfirm = null) {
    setAlertState({
      isOpen: true,
      message,
      onConfirm,
    });
  }

  // Alert 닫기 함수
  function closeAlert() {
    // 콜백을 먼저 저장 (state 변경 전에 참조)
    const callback = alertState.onConfirm;
    // state 먼저 초기화
    setAlertState({
      isOpen: false,
      message: '',
      onConfirm: null,
    });
    // 콜백이 있으면 실행
    if (callback) {
      callback();
    }
  }

  // 참조자/시행자 업데이트 핸들러
  async function handleDocumentInfoChange(updater) {
    if (!documentInfo || !docIdx || !companyCode) {
      return;
    }

    // 권한 검증: 결재완료 문서에 한해 기안자 본인만 수정 가능
    if (!docData || !enumber) {
      showAlert('사용자 정보를 확인할 수 없습니다.');
      return;
    }

    // 문서 상태 확인 (결재완료: state = 3)
    const docState = Number(docData.state);
    if (docState !== DOCUMENT_STATE.SIGNED) {
      showAlert('결재완료 문서만 참조자/시행자를 수정할 수 있습니다.');
      return;
    }

    // 기안자 확인 (sign_1)
    const drafterEnumber = Number(docData.sign_1);
    const currentEnumber = Number(enumber);

    if (drafterEnumber !== currentEnumber) {
      showAlert('참조자/시행자 수정은 기안자 본인만 가능합니다.');
      return;
    }

    const updatedInfo = typeof updater === 'function' ? updater(documentInfo) : updater;

    // 이전 값과 비교하여 실제로 변경된 항목만 업데이트
    const prevRecipients = documentInfo.recipients || [];
    const prevExecutors = documentInfo.executors || [];
    const newRecipients = updatedInfo.recipients || [];
    const newExecutors = updatedInfo.executors || [];

    // 참조자 변경 여부 확인 (enumber 기준 비교)
    const prevRecipientIds = prevRecipients
      .map((r) => r.enumber || 0)
      .sort()
      .join(',');
    const newRecipientIds = newRecipients
      .map((r) => r.enumber || 0)
      .sort()
      .join(',');
    const isRecipientsChanged = prevRecipientIds !== newRecipientIds;

    // 시행자 변경 여부 확인 (enumber 기준 비교)
    const prevExecutorIds = prevExecutors
      .map((e) => e.enumber || 0)
      .sort()
      .join(',');
    const newExecutorIds = newExecutors
      .map((e) => e.enumber || 0)
      .sort()
      .join(',');
    const isExecutorsChanged = prevExecutorIds !== newExecutorIds;

    // 변경된 항목이 없으면 리턴
    if (!isRecipientsChanged && !isExecutorsChanged) {
      return;
    }

    try {
      // 참조자가 변경된 경우에만 업데이트
      if (isRecipientsChanged) {
        const senderList = newRecipients.map((r) => ({
          name: typeof r === 'string' ? r : r.name || '',
          code: typeof r === 'string' ? 0 : r.enumber || 0,
        }));

        const response = await updateReceiverList(docIdx, companyCode, senderList);

        if (response && response.result_code === 0) {
          // 알럿 확인 후 리스트 갱신
          showAlert('참조자가 업데이트되었습니다.', async function () {
            await queryClient.invalidateQueries({ queryKey: ['documentReceiverList', docIdx] });
            await queryClient.refetchQueries({ queryKey: ['documentReceiverList', docIdx] });
          });
        } else {
          throw new Error(response?.error_msg || '참조자 업데이트에 실패했습니다.');
        }
        return;
      }

      // 시행자가 변경된 경우에만 업데이트
      if (isExecutorsChanged) {
        const enumberList = newExecutors
          .map((e) => (typeof e === 'string' ? 0 : e.enumber || 0))
          .filter((num) => num > 0);

        const response = await updateExecutorList(docIdx, enumberList);

        if (response && response.result_code === 0) {
          // 알럿 확인 후 리스트 갱신
          showAlert('시행자가 업데이트되었습니다.', async function () {
            await queryClient.invalidateQueries({ queryKey: ['documentExecutorList', docIdx] });
            await queryClient.refetchQueries({ queryKey: ['documentExecutorList', docIdx] });
          });
        } else {
          throw new Error(response?.error_msg || '시행자 업데이트에 실패했습니다.');
        }
        return;
      }
    } catch (error) {
      console.error('참조자/시행자 업데이트 실패:', error);
      showAlert(getApiErrorMessage(error, '참조자/시행자 업데이트 중 오류가 발생했습니다.'));
    }
  }

  // 첨부파일 다운로드 핸들러
  async function handleFileDownload(fileIdx) {
    if (!fileIdx) return;
    setIsDownloading(true);
    try {
      // 1. 백엔드에서 다운로드 가능한 정적 URL 조회
      const response = await downloadDocumentFile(fileIdx);

      if (response && response.result_code === 0 && response.resultData) {
        const { fileURL, fileName } = response.resultData;

        if (!fileURL) {
          throw new Error('다운로드 URL을 찾을 수 없습니다.');
        }

        // 2. 브라우저/Webview에서 다운로드 트리거
        // 물리적인 파일 경로(정적 리소스)이므로 <a> 태그를 사용하여 직접 다운로드
        const a = document.createElement('a');

        // baseUrl 처리: 외부 도메인일 경우 또는 절대 경로인 경우 대응
        const fullURL = fileURL.startsWith('http')
          ? fileURL
          : `${window.location.origin}${fileURL}`;

        a.href = fullURL;
        a.download = fileName || 'download';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        throw new Error(response?.error_msg || '다운로드 URL 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      if (error.response?.status === 404) {
        showAlert('파일을 찾을 수 없습니다.');
      } else {
        showAlert(getApiErrorMessage(error, '파일 다운로드 중 오류가 발생했습니다.'));
      }
    } finally {
      setIsDownloading(false);
    }
  }

  /**
   * [인쇄 기능 구현]
   * - useDocumentPreview 훅과 동일한 패턴 사용
   * - react-to-print 라이브러리를 사용하여 인쇄 기능 제공
   *
   * [다른 컴포넌트에서 사용 시]
   * - 동일하게 useRef + useReactToPrint 조합 사용
   * - 인쇄할 컴포넌트는 forwardRef로 래핑 필요
   * - 자세한 내용은 useDocumentPreview.js 주석 참고
   */
  // 하단 버튼 핸들러
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // 인쇄할 컴포넌트에 전달할 ref (forwardRef로 래핑된 컴포넌트에 연결)
  const printContentRef = useRef(null);

  async function handlePreview() {
    if (!docData) return;

    const useCorporateSeal =
      docData.is_corp_seal_used === 1 ||
      docData.is_corp_seal_used === '1' ||
      docData.isCorpSealUsed === 1 ||
      docData.isCorpSealUsed === '1';

    const rawContent = docData.contents || '';
    const editorContent = normalizeHtmlForCKEditor(rawContent, {
      removeFontSize: false,
      stripNegativeTextIndent: true,
    });
    const summaryNote = docData.summary || docData.note || docData.summary_note || '';

    setIsCapturing(true);
    try {
      // 페이지 레이아웃 정보 계산
      const layout = calculatePageCount({
        editorContent,
        documentInfo,
        summaryNote,
        attachments,
        referenceDocuments,
        useCorporateSeal,
        returnLayout: true,
      });

      // 1. 본문 이미지 캡처 — DOCUMENT_CAPTURE_DEFAULTS(공통 옵션) 사용.
      // captureHtmlToPngLayout 내부에서 normalizeHtmlForCKEditor를 실행하므로 여기서 따로 하지 않음.
      const captureResult = await captureHtmlAsDataUrl(editorContent, {
        ...DOCUMENT_CAPTURE_DEFAULTS,
      });

      if (captureResult) {
        // 2. 이미지 기반 슬라이스 계산
        const slices = calculateImageSlices(captureResult.height, layout.layout);
        setPreviewImageData({
          url: captureResult.dataUrl,
          slices: slices,
          width: captureResult.width,
        });
        setCalculatedTotalPages(slices.length);
      } else {
        // 폴백
        const totalPages = calculatePageCount({
          editorContent,
          documentInfo,
          summaryNote,
          attachments,
          referenceDocuments,
          useCorporateSeal,
        });
        setCalculatedTotalPages(totalPages);

        const contentByPage = splitEditorContentByPage({
          editorContent,
          totalPages,
          documentInfo,
          useCorporateSeal,
          summaryNote,
          attachments,
          referenceDocuments,
        });
        setEditorContentByPage(contentByPage);
        setPreviewImageData(null);
      }

      setIsPreviewMode(true);
    } catch (error) {
      console.error('Preview failed:', error);
      showAlert('미리보기를 준비하는 중 오류가 발생했습니다.');
    } finally {
      setIsCapturing(false);
    }
  }

  function handleClosePreview() {
    setIsPreviewMode(false);
  }

  /**
   * react-to-print를 사용한 인쇄 핸들러
   * - useDocumentPreview.js의 handlePrint와 동일한 패턴
   * - 자세한 설명은 useDocumentPreview.js 주석 참고
   */
  const handlePrint = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: '전자결재 문서',
    onBeforeGetContent: function () {
      // 미리보기 모드가 아닌 경우 미리보기 모드로 전환
      // 인쇄 전에 최신 데이터가 반영되도록 보장
      if (!isPreviewMode) {
        handlePreview();
        // 미리보기 모드로 전환 후 약간의 지연을 두고 인쇄
        // (상태 업데이트 및 렌더링 완료 대기)
        return new Promise(function (resolve) {
          setTimeout(function () {
            resolve();
          }, 300);
        });
      }
      return Promise.resolve();
    },
  });

  function handleRequestModification() {
    // 현재 사용자의 결재 위치 확인
    if (!myPosition) {
      showAlert('결재 위치를 찾을 수 없습니다.');
      return;
    }

    // InputModal 열기
    setIsInputModalOpen(true);
    setInputModalValue('');
  }

  // InputModal 확인 버튼 클릭 핸들러
  async function handleInputModalConfirm() {
    // 텍스트가 비어있으면 알림
    if (!inputModalValue.trim()) {
      showAlert('사유를 입력해주세요.');
      return;
    }

    // 500자 초과 검증
    if (inputModalValue.length > 500) {
      showAlert('사유는 500자까지 입력 가능합니다.');
      return;
    }

    if (!docIdx || !myPosition) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const response = await returnDocument(
        Number(docIdx),
        myPosition,
        DOCUMENT_STATE.RETURNEDIT, // 6: 반려(수정)
        inputModalValue.trim(),
      );

      if (response && response.result_code === 0) {
        setIsInputModalOpen(false);
        setInputModalValue('');
        // 알럿 띄우기 전에 즉시 목록 리패치 (확인 클릭 시 모달만 닫기)
        queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
        queryClient.invalidateQueries({ queryKey: ['approvalList'] });
        queryClient.invalidateQueries({ queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)] });
        await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
        await queryClient.refetchQueries({ queryKey: ['approvalList'] });
        await queryClient.refetchQueries({ queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)] });
        showAlert('수정요청이 완료되었습니다.', function () {
          onClose();
        });
      } else {
        throw new Error(response?.error_msg || '수정요청 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('수정요청 실패:', error);
      showAlert(getApiErrorMessage(error, '수정요청 처리 중 오류가 발생했습니다.'));
    }
  }

  // InputModal 취소 버튼 클릭 핸들러
  function handleInputModalCancel() {
    setIsInputModalOpen(false);
    setInputModalValue('');
  }

  function handleReject() {
    // 현재 사용자의 결재 위치 확인
    if (!myPosition) {
      showAlert('결재 위치를 찾을 수 없습니다.');
      return;
    }

    // 반려 모달 열기
    setIsRejectModalOpen(true);
    setRejectModalValue('');
  }

  // 반려 InputModal 확인 버튼 클릭 핸들러
  async function handleRejectModalConfirm() {
    // 텍스트가 비어있으면 알림
    if (!rejectModalValue.trim()) {
      showAlert('반려 사유를 입력해주세요.');
      return;
    }

    // 500자 초과 검증
    if (rejectModalValue.length > 500) {
      showAlert('반려 사유는 500자까지 입력 가능합니다.');
      return;
    }

    if (!docIdx || !myPosition) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const response = await returnDocument(
        Number(docIdx),
        myPosition,
        DOCUMENT_STATE.RETURN, // 4: 반려
        rejectModalValue.trim(),
      );

      if (response && response.result_code === 0) {
        setIsRejectModalOpen(false);
        setRejectModalValue('');
        // 알럿 띄우기 전에 즉시 목록 리패치 (확인 클릭 시 모달만 닫기)
        queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
        queryClient.invalidateQueries({ queryKey: ['approvalList'] });
        queryClient.invalidateQueries({ queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)] });
        await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
        await queryClient.refetchQueries({ queryKey: ['approvalList'] });
        await queryClient.refetchQueries({ queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)] });
        showAlert('반려가 완료되었습니다.', function () {
          onClose();
        });
      } else {
        throw new Error(response?.error_msg || '반려 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('반려 실패:', error);
      showAlert(getApiErrorMessage(error, '반려 처리 중 오류가 발생했습니다.'));
    }
  }

  // 반려 InputModal 취소 버튼 클릭 핸들러
  function handleRejectModalCancel() {
    setIsRejectModalOpen(false);
    setRejectModalValue('');
  }

  function handleApprove() {
    // 현재 사용자의 결재 위치 확인
    if (!myPosition) {
      showAlert('결재 위치를 찾을 수 없습니다.');
      return;
    }

    // 결재 확인 모달 열기
    setIsApproveConfirmOpen(true);
  }

  // 결재 확인 모달에서 "예" 클릭 핸들러
  async function handleApproveConfirm() {
    if (!docIdx || !myPosition) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    try {
      let response;

      // 결재자 (sign2~5)
      if (myPosition.startsWith('sign')) {
        const signNumber = parseInt(myPosition.replace('sign', ''), 10);
        response = await postApprovalSign({
          signNumber,
          idx: Number(docIdx),
        });
      }
      // 검토자 (check1~4)
      else if (myPosition.startsWith('check')) {
        const checkNumber = parseInt(myPosition.replace('check', ''), 10);
        response = await postApprovalReviewerDone({
          checkNumber,
          idx: Number(docIdx),
        });
      }
      // 합의자
      else if (myPosition === 'agreement') {
        response = await postApprovalAgreementDone({
          idx: Number(docIdx),
        });
      } else {
        throw new Error('알 수 없는 결재 위치입니다.');
      }

      if (response && response.result_code === 0) {
        setIsApproveConfirmOpen(false);
        queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
        queryClient.invalidateQueries({ queryKey: ['approvalList'] });
        queryClient.invalidateQueries({ queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)] });
        await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
        await queryClient.refetchQueries({ queryKey: ['approvalList'] });
        await queryClient.refetchQueries({ queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)] });
        showAlert('결재가 완료되었습니다.', function () {
          onClose();
        });
      } else {
        throw new Error(response?.error_msg || '결재 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('결재 실패:', error);
      setIsApproveConfirmOpen(false);
      showAlert(getApiErrorMessage(error, '결재 처리 중 오류가 발생했습니다.'));
    }
  }

  // 결재 확인 모달에서 "아니오" 클릭 핸들러
  function handleApproveCancel() {
    setIsApproveConfirmOpen(false);
  }

  // 코멘트 작성 버튼 클릭 핸들러
  function handleCommentWrite() {
    setIsCommentModalOpen(true);
    setCommentModalValue('');
  }

  // 코멘트 작성 모달 확인 버튼 클릭 핸들러
  async function handleCommentModalConfirm() {
    // 텍스트가 비어있으면 알림
    if (!commentModalValue.trim()) {
      showAlert('코멘트 내용을 입력해주세요.');
      return;
    }

    // 500자 초과 검증
    if (commentModalValue.length > 500) {
      showAlert('코멘트는 500자까지 입력 가능합니다.');
      return;
    }

    if (!docIdx) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const response = await addDocumentComment(Number(docIdx), commentModalValue.trim());

      if (response && response.result_code === 0) {
        showAlert('코멘트가 등록되었습니다.');
        setIsCommentModalOpen(false);
        setCommentModalValue('');
        // 코멘트 목록 갱신을 위해 쿼리 무효화
        queryClient.invalidateQueries({ queryKey: ['documentCommentList', docIdx] });
        await queryClient.refetchQueries({ queryKey: ['documentCommentList', docIdx] });
      } else {
        throw new Error(response?.error_msg || '코멘트 등록에 실패했습니다.');
      }
    } catch (error) {
      console.error('코멘트 등록 실패:', error);
      showAlert(getApiErrorMessage(error, '코멘트 등록 중 오류가 발생했습니다.'));
    }
  }

  // 코멘트 작성 모달 취소 버튼 클릭 핸들러
  function handleCommentModalCancel() {
    setIsCommentModalOpen(false);
    setCommentModalValue('');
  }

  // 코멘트 클릭 핸들러 (코멘트 상세 모달 열기)
  function handleCommentClick(comment) {
    if (!comment) return;
    setSelectedComment(comment);
    setIsCommentDetailModalOpen(true);
  }

  // 코멘트 상세 모달 닫기 핸들러
  function handleCommentDetailClose() {
    setIsCommentDetailModalOpen(false);
    setSelectedComment(null);
  }

  // 코멘트 수정 버튼 클릭 핸들러 (상세 모달에서 수정 버튼 클릭)
  function handleCommentModify() {
    if (!selectedComment) return;
    // 상세 모달 닫고 수정 모달 열기
    setIsCommentDetailModalOpen(false);
    setCommentEditValue(selectedComment.comment || '');
    setIsCommentEditModalOpen(true);
  }

  // 코멘트 수정 모달 확인 버튼 클릭 핸들러
  async function handleCommentEditConfirm() {
    if (!commentEditValue.trim()) {
      showAlert('코멘트 내용을 입력해주세요.');
      return;
    }

    // 500자 초과 검증
    if (commentEditValue.length > 500) {
      showAlert('코멘트는 500자까지 입력 가능합니다.');
      return;
    }

    // API 응답: commentIdx 필드 사용 (백엔드 ResponseCommentInfoDTO)
    const commentIdx = selectedComment.commentIdx || selectedComment.idx;
    if (!commentIdx || !docIdx) {
      showAlert('코멘트 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const response = await modifyDocumentComment(
        commentIdx,
        Number(docIdx),
        commentEditValue.trim(),
      );

      if (response && response.result_code === 0) {
        showAlert('코멘트가 수정되었습니다.');
        setIsCommentEditModalOpen(false);
        setCommentEditValue('');
        setSelectedComment(null);
        // 코멘트 목록 갱신을 위해 쿼리 무효화
        queryClient.invalidateQueries({ queryKey: ['documentCommentList', docIdx] });
        await queryClient.refetchQueries({ queryKey: ['documentCommentList', docIdx] });
      } else {
        throw new Error(response?.error_msg || '코멘트 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('코멘트 수정 실패:', error);
      showAlert(getApiErrorMessage(error, '코멘트 수정 중 오류가 발생했습니다.'));
    }
  }

  // 코멘트 수정 모달 취소 버튼 클릭 핸들러
  function handleCommentEditCancel() {
    setIsCommentEditModalOpen(false);
    setCommentEditValue('');
    // 수정 취소 시 상세 모달 다시 열기
    setIsCommentDetailModalOpen(true);
  }

  // 코멘트 삭제 버튼 클릭 핸들러
  async function handleCommentDelete() {
    // API 응답: commentIdx 필드 사용 (백엔드 ResponseCommentInfoDTO)
    const commentIdx = selectedComment.commentIdx || selectedComment.idx;
    if (!commentIdx || !docIdx) {
      showAlert('코멘트 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const response = await deleteDocumentComment(commentIdx, Number(docIdx));

      if (response && response.result_code === 0) {
        showAlert('코멘트가 삭제되었습니다.');
        setIsCommentDetailModalOpen(false);
        setSelectedComment(null);
        // 코멘트 목록 갱신을 위해 쿼리 무효화
        queryClient.invalidateQueries({ queryKey: ['documentCommentList', docIdx] });
        await queryClient.refetchQueries({ queryKey: ['documentCommentList', docIdx] });
      } else {
        throw new Error(response?.error_msg || '코멘트 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('코멘트 삭제 실패:', error);
      showAlert(getApiErrorMessage(error, '코멘트 삭제 중 오류가 발생했습니다.'));
    }
  }

  // 수정 버튼 핸들러 (문서 작성 페이지로 이동)
  function handleModify() {
    if (!docIdx || !docData || !documentInfo) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    // 모달 닫기
    onClose();

    // 문서 작성 페이지로 이동 (수정 모드) - 모든 데이터 전달
    navigate('/approval/new-document/write', {
      state: {
        docIdx: Number(docIdx),
        isEditMode: true,
        // 기존 문서 상태 (재상신 시 백엔드에서 사용)
        docState: docData.state || 6,
        // 양식 정보 (API는 doc_type/docType 반환, doc 필드는 없을 수 있음)
        formInfo: {
          formName: docData.title || docData.doc_title || '',
          formId: docData.doc ?? docData.doc_type ?? docData.docType ?? null,
          formContent: docData.contents || '',
        },
        // 문서 정보
        documentInfo: documentInfo,
        // 에디터 내용
        editorContent: docData.contents || '',
        // 참조문서
        referenceDocuments: referenceDocuments,
        // 첨부파일
        attachments: attachments,
        // 요약 및 비고
        summaryNote: docData.summary || docData.note || docData.summary_note || '',
        // 법인 인감 사용 여부
        useCorporateSeal:
          docData.is_corp_seal_used === 1 ||
          docData.is_corp_seal_used === '1' ||
          docData.isCorpSealUsed === 1 ||
          docData.isCorpSealUsed === '1',
      },
    });
  }

  // 재작성 버튼 핸들러 (반려된 문서를 새 문서로 재작성)
  // - 양식 내용(contents)만 유지
  // - 결재라인, 참조자, 시행자는 양식 기본값으로 초기화
  // - 참조문서, 첨부파일, 요약 및 비고는 초기화
  // - 새 문서로 상신 (mode=0)
  function handleRewrite() {
    if (!docIdx || !docData) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    // 모달 닫기
    onClose();

    // 문서 작성 페이지로 이동 (재작성 모드) - 양식 내용만 전달
    navigate('/approval/new-document/write', {
      state: {
        isRewriteMode: true,
        formInfo: {
          formName: docData.title || docData.doc_title || '',
          formId: docData.doc ?? docData.doc_type ?? docData.docType ?? null,
          formContent: docData.contents || '',
        },
        editorContent: docData.contents || '',
      },
    });
  }

  // 삭제 버튼 핸들러
  async function handleDelete() {
    if (!docIdx) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const response = await removeApprovalDoc(Number(docIdx));

      if (response && response.result_code === 0) {
        showAlert('문서가 삭제되었습니다.');
        queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
        queryClient.invalidateQueries({ queryKey: ['approvalList'] });
        await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
        await queryClient.refetchQueries({ queryKey: ['approvalList'] });
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(response?.error_msg || '문서 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('문서 삭제 실패:', error);
      showAlert(getApiErrorMessage(error, '문서 삭제 중 오류가 발생했습니다.'));
    }
  }

  // 참조문서 클릭 핸들러 (중첩 모달 열기)
  function handleReferenceClick(doc) {
    if (doc?.idx) {
      setStackedDocIds((prev) => [...prev, doc.idx]);
    }
  }

  // 중첩 모달 닫기 핸들러
  function handleStackedClose() {
    setStackedDocIds((prev) => prev.slice(0, -1));
  }

  // 기안일자 변경 핸들러
  function handleDraftDateChange(updater) {
    const updatedInfo = typeof updater === 'function' ? updater(documentInfo) : updater;
    setEditedDraftDate(updatedInfo.draftDate);
  }

  // 기안일자 저장 핸들러
  async function handleDraftDateSave() {
    if (!docIdx || !editedDraftDate) return;

    // 기안자 enumber 가져오기 (sign_1)
    const drafterEnumber = docData?.sign_1;
    if (!drafterEnumber) {
      showAlert('기안자 정보를 찾을 수 없습니다.');
      return;
    }

    setIsSavingDraftDate(true);
    try {
      const response = await updateApprovalDocDate({
        idx: Number(docIdx),
        date: editedDraftDate,
        enumber: Number(drafterEnumber),
      });

      if (response && response.result_code === 0) {
        showAlert('저장이 완료되었습니다.', async function () {
          // 리스트 갱신
          queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
          queryClient.invalidateQueries({ queryKey: ['approvalList'] });
          queryClient.invalidateQueries({ queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)] });
          await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
          await queryClient.refetchQueries({ queryKey: ['approvalList'] });
          await queryClient.refetchQueries({
            queryKey: [DOCUMENT_DETAIL_QUERY_KEY, Number(docIdx)],
          });

          // 부모 컴포넌트 리프레시 콜백 (예: 어드민 페이지)
          if (onSaveSuccess) {
            onSaveSuccess();
          }

          // 모달 닫기
          onClose();
        });
      } else {
        throw new Error(response?.error_msg || '저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('기안일자 저장 실패:', error);
      showAlert(getApiErrorMessage(error, '저장 중 오류가 발생했습니다.'));
    } finally {
      setIsSavingDraftDate(false);
    }
  }

  // 관리자 전용 문서 삭제 핸들러 (상태를 5로 변경)
  async function handleAdminDelete() {
    if (!docIdx) {
      showAlert('문서 정보가 올바르지 않습니다.');
      return;
    }

    try {
      const response = await removeDocument(Number(docIdx));

      if (response && response.result_code === 0) {
        showAlert('삭제되었습니다.', async function () {
          // 리스트 갱신
          queryClient.invalidateQueries({ queryKey: ['adminDocumentList'] }); // 어드민 리스트 키 (관리자 페이지에 따라 확인 필요)
          queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
          queryClient.invalidateQueries({ queryKey: ['approvalList'] });
          await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
          await queryClient.refetchQueries({ queryKey: ['approvalList'] });

          // 부모 컴포넌트 리프레시 콜백
          if (onSaveSuccess) {
            onSaveSuccess();
          }

          // 모달 닫기
          onClose();
        });
      } else {
        throw new Error(response?.error_msg || '삭제 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('관리자 문서 삭제 실패:', error);
      showAlert(getApiErrorMessage(error, '삭제 처리 중 오류가 발생했습니다.'));
    }
  }

  return {
    // 상태
    alertState,
    isInputModalOpen,
    inputModalValue,
    isRejectModalOpen,
    rejectModalValue,
    isApproveConfirmOpen,
    isCommentModalOpen,
    commentModalValue,
    isCommentDetailModalOpen,
    selectedComment,
    isCommentEditModalOpen,
    commentEditValue,
    stackedDocIds,
    myPosition,

    // 상태 세터
    setInputModalValue,
    setRejectModalValue,
    setCommentModalValue,
    setCommentEditValue,

    // 핸들러
    showAlert,
    closeAlert,
    handleDocumentInfoChange,
    handleFileDownload,
    isPreviewMode,
    printContentRef,
    handlePreview,
    handleClosePreview,
    handlePrint,
    handleRequestModification,
    handleInputModalConfirm,
    handleInputModalCancel,
    handleReject,
    handleRejectModalConfirm,
    handleRejectModalCancel,
    handleApprove,
    handleApproveConfirm,
    handleApproveCancel,
    handleCommentWrite,
    handleCommentModalConfirm,
    handleCommentModalCancel,
    handleCommentClick,
    handleCommentDetailClose,
    handleCommentModify,
    handleCommentEditConfirm,
    handleCommentEditCancel,
    handleCommentDelete,
    handleModify,
    handleRewrite,
    handleDelete,
    handleReferenceClick,
    handleStackedClose,
    calculatedTotalPages,
    editorContentByPage,
    previewImageData,
    isDownloading,
    isCapturing,
    editedDraftDate,
    handleDraftDateChange,
    handleDraftDateSave,
    isSavingDraftDate,
    finalDocumentInfo,
    handleAdminDelete,
  };
}

export default useDocumentActions;
